import { Injectable } from "@angular/core";
import { Appointment } from "../models/admissions.model";
import { BroadcastEventTypes } from "../models/lookup.model";
import { chartEncounterActivityPayload } from "../helpers/activity-encounter-context.util";
import { ActivityNotesService } from "./activity-notes.service";
import { ApplicationCacheService } from "./application-cache.service";
import { ApplicationContextService } from "./application-context.service";
import { EventService } from "./event.service";

export const APPOINTMENT_CONFIRMED_ACTIVITY_TYPE = "Appointment Confirmed";
export const APPOINTMENT_UNCONFIRMED_ACTIVITY_TYPE = "Appointment Unconfirmed";

@Injectable({
  providedIn: "root",
})
export class ConfirmationActivityNoteService {
  constructor(
    private readonly _activityNotesService: ActivityNotesService,
    private readonly _applicationCacheService: ApplicationCacheService,
    private readonly _applicationContextService: ApplicationContextService,
    private readonly _eventService: EventService
  ) {}

  /** Writes a clinic activity when an appointment is confirmed or unconfirmed. */
  log(updated: Appointment | null | undefined, confirmed: boolean): void {
    const encounterId = String(updated?.id || "").trim();
    const patientId = String(
      updated?.patientId || updated?.encountersPatient?.id || ""
    ).trim();
    const procedureId = this.procedureId(updated);
    if (!encounterId || !patientId || !procedureId) {
      return;
    }

    const activityBy = this.currentUserName();
    const when = new Date().toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
    const notes = confirmed
      ? `Confirmed by ${activityBy} on ${when}`
      : `Unconfirmed by ${activityBy} on ${when}`;

    this._activityNotesService
      .addNewActivityNote(
        chartEncounterActivityPayload({
          patientId,
          procedureId,
          encounterId,
          notes,
          activityBy,
          activityType: confirmed
            ? APPOINTMENT_CONFIRMED_ACTIVITY_TYPE
            : APPOINTMENT_UNCONFIRMED_ACTIVITY_TYPE,
          displayMode: "both",
        })
      )
      .subscribe({
        next: (activityNote) => {
          if (!activityNote?.id) {
            return;
          }
          this._applicationCacheService.upsertProcedureRoomNurseActivityNoteCache(
            activityNote
          );
          this._eventService.broadcast(
            BroadcastEventTypes.ActivityNoteAdded,
            activityNote
          );
        },
        error: () => {},
      });
  }

  private procedureId(updated: Appointment | null | undefined): string {
    const procedure = updated?.encounterProcedure as
      | { id?: string }
      | { id?: string }[]
      | null
      | undefined;
    const fromResponse = Array.isArray(procedure)
      ? procedure[0]?.id
      : procedure?.id;
    if (fromResponse) {
      return String(fromResponse).trim();
    }
    const cached = this._applicationCacheService.applicationCache.appointment;
    if (cached?.id && cached.id === updated?.id) {
      return String(cached.encounterProcedure?.id || "").trim();
    }
    return "";
  }

  private currentUserName(): string {
    const user = this._applicationContextService.contextSnapshot
      ?.currentlyLoggedInUser as
      | { firstName?: string; lastName?: string }
      | undefined;
    const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    return name || "System";
  }
}
